'use client';

import { type FC, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { GameV1 } from '@/api/games';
import { SectionTitle } from '@/components/SectionTitle';
import GamesSkeleton from '@/components/skeletons/GamesSkeleton';
import {
  type ISearchResultAppearanceEventData,
  sendSearchResultAppearanceEvent,
  sendSearchResultGameOpenEvent,
  type TSearchResultGameOpenType,
} from '@/modules/googleAnalytics/events/searchGamesEvents';
import { useGetCommonStore } from '@/store';
import { perChunkSearch } from '../helpers';
import { useCustomSearchScenario } from '../hooks/useCustomSearchScenarios';
import useSearchCorrection from '../hooks/useSearchCorrection';
import { useSearchGamesInfinite } from '../hooks/useSearchGamesInfinite';
import { ScResult } from '../styled';
import { getGridOptions } from './GameGridBlock';
import { SearchFallbackScenarios } from './SearchFallbackScenarios';
import {
  checkIncludesKeywords,
  JACKPOT_SCENARIO_KEYWORD,
  PROMOCODES_SCENARIO_KEYWORDS,
  THEME_SCENARIO_KEYWORD,
  WITHDRAWAL_SCENARIO_KEYWORDS,
} from './SearchFallbackScenarios/helpers';
import { SearchGameCategories } from './SearchGameCategories';
import { SearchResult } from './SearchResult';
export interface ISearchGridOptions {
  gridOptions: {
    columns: number;
    columnsTablet: number;
    columnsMobile: number;
    columnsMobileS: number;
    columnGap: number;
    rowGap: number;
  };
  plugGray: boolean;
  isSearchModal: boolean;
}

export interface ISearchGridOptions {
  gridOptions: {
    columns: number;
    columnsTablet: number;
    columnsMobile: number;
    columnsMobileS: number;
    columnGap: number;
    rowGap: number;
  };
  plugGray: boolean;
  isSearchModal: boolean;
}

export interface ISearchGridOptions {
  gridOptions: {
    columns: number;
    columnsTablet: number;
    columnsMobile: number;
    columnsMobileS: number;
    columnGap: number;
    rowGap: number;
  };
  plugGray: boolean;
  isSearchModal: boolean;
}

export interface ISearchGridOptions {
  gridOptions: {
    columns: number;
    columnsTablet: number;
    columnsMobile: number;
    columnsMobileS: number;
    columnGap: number;
    rowGap: number;
  };
  plugGray: boolean;
  isSearchModal: boolean;
}

interface IResultWithSimilarProps {
  searchPhrase: string;
  closeSearchModal?: () => void;
}

export const ResultWithSimilar: FC<IResultWithSimilarProps> = ({
  searchPhrase,
  closeSearchModal,
}) => {
  const { t } = useTranslation('common');

  const useCommonStore = useGetCommonStore();

  const isMobileDevice = useCommonStore.use.isMobileDevice();

  const { scenario } = useCustomSearchScenario(searchPhrase);

  const { correctedQuery: correctedSearchPhrase, correctedMessage } =
    useSearchCorrection(searchPhrase, scenario);

  const { games, loadMore, totalCount, pending, isLoading } =
    useSearchGamesInfinite({
      searchPhrase: correctedSearchPhrase,
    });

  const sendSearchResultAppearance = useCallback(
    (params: Partial<ISearchResultAppearanceEventData>) => {
      sendSearchResultAppearanceEvent({
        search_text: searchPhrase,
        search_text_sent: correctedSearchPhrase,
        ...params,
      });
    },
    [searchPhrase, correctedSearchPhrase]
  );

  const gridOptions = useMemo(
    () => ({
      gridOptions: getGridOptions(isMobileDevice),
      plugGray: isMobileDevice,
      isSearchModal: true,
      perChunkObj: perChunkSearch,
    }),
    [isMobileDevice]
  );

  const handleSearchGameClick = useCallback(
    (game: GameV1, type: TSearchResultGameOpenType) => {
      sendSearchResultGameOpenEvent({
        type,
        provider: game.provider,
        game_name: game.title,
      });
      closeSearchModal?.();
    },
    [closeSearchModal]
  );

  const searchResult = useMemo(() => {
    if (!correctedSearchPhrase) return;

    if (isLoading && !games.length) return <GamesSkeleton isSearchModal />;

    if (!games.length)
      return (
        <SearchFallbackScenarios
          closeModal={closeSearchModal}
          gridOptions={gridOptions}
          handleSearchGameClick={handleSearchGameClick}
          searchPhrase={correctedSearchPhrase}
          sendSearchResultAppearance={sendSearchResultAppearance}
        />
      );

    return (
      <SearchResult
        games={games}
        gridOptions={gridOptions}
        handleClickShowMore={loadMore}
        handleSearchGameClick={handleSearchGameClick}
        loading={pending}
        sendSearchResultAppearance={sendSearchResultAppearance}
        totalCount={totalCount}
      />
    );
  }, [
    games,
    isLoading,
    pending,
    gridOptions,
    correctedSearchPhrase,
    totalCount,
    loadMore,
    handleSearchGameClick,
    sendSearchResultAppearance,
    closeSearchModal,
  ]);

  const showGeneralSearchGategories = useMemo(() => {
    if (!correctedSearchPhrase) return true;
    if (
      checkIncludesKeywords(correctedSearchPhrase, [
        ...WITHDRAWAL_SCENARIO_KEYWORDS,
        ...PROMOCODES_SCENARIO_KEYWORDS,
        JACKPOT_SCENARIO_KEYWORD,
        THEME_SCENARIO_KEYWORD,
      ])
    )
      return true;
  }, [correctedSearchPhrase]);

  return (
    <>
      <ScResult>
        {correctedMessage && <p>{correctedMessage}</p>}
        {searchPhrase && <SectionTitle>{t('searchResults')}</SectionTitle>}
        {searchResult}
      </ScResult>
      {showGeneralSearchGategories && (
        <SearchGameCategories
          gridOptions={gridOptions}
          handleSearchGameClick={handleSearchGameClick}
        />
      )}
    </>
  );
};
